import { LoadingState } from '../ui';
// src/pages/TrophyRoom.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import ShareButton from '../components/ShareButton';
import notify from '../lib/notify';
import { Button as FcButton, Card as FcCard } from '../ui';

export default function TrophyRoom({ session, userRole, username }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    competitions: {
      total: 0,
      won: 0,
      seasonWins: 0,
      knockoutWins: 0,
      winRatio: 0
    },
    scout: {
      totalEntries: 0,
      totalPoints: 0,
      averagePoints: 0
    },
    teamSelections: {
      mostSelected: [],
      mostAvoided: []
    }
  });
  
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  useEffect(() => {
    if (!session?.user?.id) return;
    fetchTrophyRoomData();
  }, [session?.user?.id]);

  const fetchTrophyRoomData = async () => {
    try {
      const userId = session.user.id;
      let competitionStats = [];
      let scoutEntries = [];
      let selections = [];

      // Fetch competition stats
      try {
        const { data: compData, error: compError } = await supabase
          .from('trophy_room_stats_view')
          .select('competition_id, competition_name, competition_type, competition_status, user_status, rank, is_winner, points, wins, draws, losses, goals_for, goals_against, total_participants')
          .eq('user_id', userId);

        if (compError) {
          notify('Failed to load competition statistics', 'error');
        } else {
          competitionStats = compData || [];
        }
      } catch (err) {
      }

      // Fetch scout entries
      try {
        const { data: scoutData, error: scoutError } = await supabase
          .from('scout_entries')
          .select('total_points, match_day')
          .eq('user_id', userId);

        if (scoutError) {
          notify('Failed to load scout entries', 'error');
        } else {
          scoutEntries = scoutData || [];
        }
      } catch (err) {
      }

      // Fetch team selections for analysis (same approach as CompetitionView)
      try {
        const { data: selData, error: selError } = await supabase
          .from('team_selections')
          .select('*') // Get all columns to see what's available
          .eq('user_id', userId);

        if (selError) {
          notify('Failed to load team selections', 'error');
        } else {
          // Map to expected format for analysis - check different possible column names
          selections = (selData || []).map(selection => {
            // Try different possible column names for team
            const teamName = selection.team_name || 
                            selection.team || 
                            selection.selected_team || 
                            selection.team_selection ||
                            'Unknown Team';
            
            return {
              team_selection: teamName,
              competition_id: selection.competition_id,
              league: selection.league,
              season: selection.season
            };
          });
        }
      } catch (err) {
      }

      // Calculate competition statistics (excluding scout competitions from main stats)
      const mainCompetitions = competitionStats.filter(comp => comp.competition_type !== 'scout');
      const totalCompetitions = mainCompetitions.length;
      const wonCompetitions = competitionStats.filter(comp => comp.is_winner === true && comp.competition_type !== 'scout').length;
      const seasonWins = competitionStats.filter(comp => comp.competition_type === 'season' && comp.is_winner === true).length;
      const knockoutWins = competitionStats.filter(comp => comp.competition_type === 'knockout' && comp.is_winner === true).length;
      const winRatio = totalCompetitions > 0 ? ((wonCompetitions / totalCompetitions) * 100) : 0;

      // Calculate scout statistics
      const totalScoutEntries = scoutEntries.length;
      const totalScoutPoints = scoutEntries.reduce((sum, entry) => sum + (entry.total_points || 0), 0);
      const averageScoutPoints = totalScoutEntries > 0 ? (totalScoutPoints / totalScoutEntries) : 0;

      // Analyze team selections with proper error handling
      const teamCounts = {};
      let mostSelected = [];
      let mostAvoided = [];
      
      try {
        // Safely process selections array
        const validSelections = Array.isArray(selections) ? selections : [];
        
        validSelections.forEach(selection => {
          if (selection && selection.team_selection) {
            teamCounts[selection.team_selection] = (teamCounts[selection.team_selection] || 0) + 1;
          }
        });

        const sortedTeams = Object.entries(teamCounts).sort(([,a], [,b]) => b - a);
        const totalSelections = validSelections.length;
        
        // Only create arrays if we have data
        if (sortedTeams.length > 0) {
          mostSelected = sortedTeams.slice(0, 5).map(([team, count]) => ({ 
            team, 
            count, 
            percentage: totalSelections > 0 ? ((count / totalSelections) * 100).toFixed(1) : '0.0'
          }));
          
          // For mostAvoided, only show teams that have lower counts than the top team
          const topTeamCount = mostSelected[0]?.count || 0;
          mostAvoided = sortedTeams
            .filter(([, count]) => count < topTeamCount)
            .slice(-5)
            .reverse()
            .map(([team, count]) => ({ 
              team, 
              count, 
              percentage: totalSelections > 0 ? ((count / totalSelections) * 100).toFixed(1) : '0.0'
            }));
        }
      } catch (err) {
        // Set safe fallbacks
        mostSelected = [];
        mostAvoided = [];
      }

      setStats({
        competitions: {
          total: totalCompetitions,
          won: wonCompetitions,
          seasonWins,
          knockoutWins,
          winRatio: winRatio.toFixed(1)
        },
        scout: {
          totalEntries: totalScoutEntries,
          totalPoints: totalScoutPoints,
          averagePoints: averageScoutPoints.toFixed(1)
        },
        teamSelections: {
          mostSelected,
          mostAvoided: mostAvoided.filter(team => team.count < (mostSelected[0]?.count || 0))
        }
      });

    } catch (error) {
      notify('Failed to load trophy room data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, gradient, textColor = "text-white" }) => (
    <div className={`${gradient} rounded-xl p-6 shadow-lg transform hover:scale-105 transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`${textColor} opacity-90 text-sm font-medium`}>{title}</p>
          <p className={`${textColor} text-3xl font-bold mt-1`}>{value}</p>
          {subtitle && <p className={`${textColor} opacity-75 text-xs mt-1`}>{subtitle}</p>}
        </div>
        <div className={`text-4xl ${textColor} opacity-80`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const TabButton = ({ id, label, active, onClick }) => (
    <FcButton variant="primary" type="submit"
      onClick={() => onClick(id)}
      className={`${
        active
          ? ""
          : ""
      }`}
    >
      {label}
    </FcButton>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-background">
        <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <LoadingState label="Loading trophy room..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-background">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-20 md:pb-8 fc-workspace">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <span className="text-4xl">🏆</span>
            <div>
              <h1 className="text-3xl font-bold text-ui-green">Trophy Room</h1>
              <p className="text-content-secondary">Your competitive journey and achievements</p>
            </div>
          </div>
          {!loading && (
            <ShareButton
              url={`${window.location.origin}/trophy-room`}
              title="Check out my FootyCoach achievements!"
              text={`I have ${stats.competitions.won} wins out of ${stats.competitions.total} competitions! Scout total: ${stats.scout.totalPoints} points 🏆`}
              className="bg-action-primary hover:bg-[#a95c28] text-white px-4 py-2 rounded-lg transition-colors"
            >
              Share Achievements
            </ShareButton>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <TabButton 
            id="overview" 
            label="Overview" 
            active={activeTab === 'overview'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="competitions" 
            label="Competitions" 
            active={activeTab === 'competitions'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="scout" 
            label="Scout" 
            active={activeTab === 'scout'} 
            onClick={setActiveTab} 
          />
          <TabButton 
            id="teams" 
            label="Team Analysis" 
            active={activeTab === 'teams'} 
            onClick={setActiveTab} 
          />
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <StatCard
                title="Total Competitions"
                value={stats.competitions.total}
                subtitle="Entered"
                icon="🎯"
                gradient="bg-gradient-to-br from-[#809076] to-[#6d7a65]"
                textColor="text-[#111A19]"
              />
              <StatCard
                title="Competitions Won"
                value={stats.competitions.won}
                subtitle={`${stats.competitions.winRatio}% win rate`}
                icon="🏆"
                gradient="bg-gradient-to-br from-[#F8D794] to-[#f5d085]"
                textColor="text-[#111A19]"
              />
              <StatCard
                title="Scout Entries"
                value={stats.scout.totalEntries}
                subtitle={`${stats.scout.averagePoints} avg points`}
                icon="🔍"
                gradient="bg-gradient-to-br from-[#BA6830] to-[#9d5526]"
                textColor="text-[#111A19]"
              />
              <StatCard
                title="Scout Points"
                value={stats.scout.totalPoints}
                subtitle="Total earned"
                icon="⭐"
                gradient="bg-gradient-to-br from-[#111A19] to-[#0a0f0d]"
              />
            </div>

            {/* Achievement Highlights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">🏅</span>
                  <h3 className="text-xl font-bold text-ui-green">Competition Wins</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <span className="font-medium text-yellow-800">Season Competitions</span>
                    <span className="text-2xl font-bold text-yellow-600">{stats.competitions.seasonWins}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="font-medium text-orange-800">Knockout Competitions</span>
                    <span className="text-2xl font-bold text-orange-600">{stats.competitions.knockoutWins}</span>
                  </div>
                </div>
              </FcCard>

              {/* Performance Insights */}
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">📊</span>
                  <h3 className="text-xl font-bold text-ui-green">Performance Insights</h3>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-secondary">Win Ratio</span>
                      <span className="font-bold text-action-primary">{stats.competitions.winRatio}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-action-primary h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(stats.competitions.winRatio, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-secondary">Scout Average</span>
                      <span className="font-bold text-ui-green">{stats.scout.averagePoints} pts</span>
                    </div>
                  </div>
                </div>
              </FcCard>
            </div>
          </div>
        )}

        {activeTab === 'competitions' && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-ui-green mb-6">Competition History</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-[#809076] to-[#6d7a65] rounded-xl text-[#111A19]">
                <div className="text-4xl mb-2">🏆</div>
                <div className="text-3xl font-bold">{stats.competitions.total}</div>
                <div className="text-[#111A19] opacity-80">Total Entered</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#F8D794] to-[#f5d085] rounded-xl text-[#111A19]">
                <div className="text-4xl mb-2">🥇</div>
                <div className="text-3xl font-bold">{stats.competitions.won}</div>
                <div className="text-[#111A19] opacity-80">Championships</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#111A19] to-[#0a0f0d] rounded-xl text-white">
                <div className="text-4xl mb-2">📊</div>
                <div className="text-3xl font-bold">{stats.competitions.winRatio}%</div>
                <div className="text-white opacity-80">Win Rate</div>
              </div>
            </div>
          </FcCard>
        )}

        {activeTab === 'scout' && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-ui-green mb-6">Scout Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-ui-copper to-[#9d5526] rounded-xl text-[#111A19]">
                <div className="text-4xl mb-2">🔍</div>
                <div className="text-3xl font-bold">{stats.scout.totalEntries}</div>
                <div className="text-[#111A19] opacity-80">Scout Entries</div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#111A19] to-[#0a0f0d] rounded-xl text-white">
                <div className="text-4xl mb-2">⭐</div>
                <div className="text-3xl font-bold">{stats.scout.totalPoints}</div>
                <div className="text-white opacity-80">Total Points</div>
              </div>
            </div>
          </FcCard>
        )}

        {activeTab === 'teams' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Most Selected Teams */}
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">⭐</span>
                  <h3 className="text-xl font-bold text-ui-green">Most Selected Teams</h3>
                </div>
                <div className="space-y-3">
                  {stats.teamSelections.mostSelected && stats.teamSelections.mostSelected.length > 0 ? (
                    stats.teamSelections.mostSelected.map((team, index) => (
                      <div key={team.team} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-ui-green text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-content-primary">{team.team}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-ui-green">{team.count}</div>
                          <div className="text-xs text-content-secondary">{team.percentage}%</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-content-secondary">
                      <p>No team selection data available yet</p>
                      <p className="text-sm">Join competitions and make selections to see your preferences!</p>
                    </div>
                  )}
                </div>
              </FcCard>

              {/* Least Selected Teams */}
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">📉</span>
                  <h3 className="text-xl font-bold text-ui-green">Least Selected Teams</h3>
                </div>
                <div className="space-y-3">
                  {stats.teamSelections.mostAvoided && stats.teamSelections.mostAvoided.length > 0 ? (
                    stats.teamSelections.mostAvoided.map((team, index) => (
                      <div key={team.team} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-content-primary">{team.team}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-ui-error">{team.count}</div>
                          <div className="text-xs text-content-secondary">{team.percentage}%</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-content-secondary">
                      <p>No team preference data available</p>
                      <p className="text-sm">Need more competition data to show least selected teams</p>
                    </div>
                  )}
                </div>
              </FcCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}